/**
 * Execution Module Selectors
 *
 * Higher-level derived selectors for common execution state queries.
 * These build on the base atoms to provide convenient access patterns.
 *
 * @module execution/selectors
 */

import {loadableStateAtomFamily} from "@agenta/entities/loadable"
import {
    extractInputPortsFromSchema,
    loadableController,
    syncPromptInputKeysInParameters,
    type RunnablePort,
} from "@agenta/entities/runnable"
import {testcaseMolecule, isSystemField} from "@agenta/entities/testcase"
import {workflowMolecule} from "@agenta/entities/workflow"
import {atom, type Getter} from "jotai"
import {selectAtom} from "jotai/utils"
import {atomFamily} from "jotai-family"

import {playgroundIsChatBehaviorAtom} from "../atoms/modeOverride"
import {entityIdsAtom, playgroundNodesAtom, playgroundStoreAtom} from "../atoms/playground"
import {addUserMessageAtom} from "../chat"
import {sharedMessageIdsAtomFamily} from "../chat/messageSelectors"

import {
    activeSessionsAtomFamily,
    resultAtomFamily,
    isAnyExecutingAtomFamily,
    resultsByKeyAtomFamily,
} from "./atoms"
import {displayedEntityIdsAtom} from "./displayedEntities"
import {createExecutionItemHandle, type ExecutionItemLifecycleSnapshot} from "./executionItems"
import {splitInputsVisibility} from "./visibility"

interface WorkflowDataForInputKeys {
    data?: {
        parameters?: Record<string, unknown> | null
        schemas?: {
            inputs?: unknown
        } | null
    } | null
}

function collectCommittedInputKeys(serverData: WorkflowDataForInputKeys | null): string[] | null {
    if (!serverData) return null

    const keys: string[] = []
    const seen = new Set<string>()
    const add = (key: unknown) => {
        if (typeof key !== "string" || !key || seen.has(key)) return
        seen.add(key)
        keys.push(key)
    }

    for (const port of extractInputPortsFromSchema(serverData.data?.schemas?.inputs)) {
        add(port.key)
    }

    const parameters = serverData.data?.parameters
    if (parameters && typeof parameters === "object" && !Array.isArray(parameters)) {
        const synced = syncPromptInputKeysInParameters(parameters)
        if (!synced || typeof synced !== "object" || Array.isArray(synced)) return keys

        const stack: unknown[] = [synced]
        while (stack.length > 0) {
            const current = stack.pop()
            if (!current || typeof current !== "object" || Array.isArray(current)) continue

            const record = current as Record<string, unknown>
            const inputKeys = record.input_keys
            if (Array.isArray(inputKeys)) {
                for (const key of inputKeys) add(key)
            }

            for (const value of Object.values(record)) {
                if (value && typeof value === "object" && !Array.isArray(value)) {
                    stack.push(value)
                }
            }
        }
    }

    return keys
}

/**
 * UI-display helper. Converts a cell value to a string for rendering inside
 * a text-mode editor.
 *
 * ⚠️ MUST NOT be used to build invocation payloads. Native JSON must reach
 * the runtime as native JSON (RFC: "native JSON stays native until template
 * rendering"). The invocation path reads testcase data directly from
 * `loadableController.selectors.row(...).data` — preserves native types.
 *
 * This helper feeds only the controller-surface atoms
 * (`rowVariableValueAtomFamily`, `testcaseCellValueAtomFamily`) that hand
 * string output to React components for the text-mode SharedEditor. When the
 * V2 input UX adds Form / JSON / YAML modes (Step 3), those modes consume
 * the native value directly via `testcaseMolecule.atoms.cell(...)` — they
 * do not route through this helper.
 */
const toDisplayString = (value: unknown): string => {
    if (value === undefined || value === null) return ""
    if (typeof value === "string") return value
    if (typeof value === "number" || typeof value === "boolean") return String(value)

    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

// ============================================================================
// CONTEXT SELECTORS (derived from playground state)
// ============================================================================

/**
 * Derived loadableId from primary node
 *
 * This allows execution to work without manually constructing loadableId.
 * Format: "testset:{entityType}:{entityId}"
 *
 * The loadable ID is anchored to the first entity that was selected, and
 * remains stable across column reordering in comparison mode. It only
 * changes when the anchored entity is removed from the playground.
 *
 * @returns Atom for the derived loadableId or empty string if no primary node
 *
 * @example
 * const loadableId = useAtomValue(derivedLoadableIdAtom)
 * // Returns "testset:appRevision:rev-123" if primary node is an app revision
 */
let _loadableAnchorEntityId: string | null = null
export const derivedLoadableIdAtom = atom((get) => {
    const rootNodes = get(playgroundNodesAtom).filter((n) => n.depth === 0)
    if (rootNodes.length === 0) {
        _loadableAnchorEntityId = null
        return ""
    }

    // If the anchor entity is still present, keep using it so that
    // reordering columns in comparison mode doesn't orphan stored data.
    if (_loadableAnchorEntityId) {
        const anchorNode = rootNodes.find((n) => n.entityId === _loadableAnchorEntityId)
        if (anchorNode) {
            return `testset:${anchorNode.entityType}:${anchorNode.entityId}`
        }
    }

    // No valid anchor — set to first available node
    const first = rootNodes[0]
    _loadableAnchorEntityId = first.entityId
    return `testset:${first.entityType}:${first.entityId}`
})

/**
 * Number of hidden testcase IDs in the current loadable.
 *
 * Changes when the user removes/hides testcase rows.
 * Used by the OSS URL layer to trigger snapshot re-encoding.
 */
export const hiddenTestcaseCountAtom = atom((get) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return 0
    const state = get(loadableStateAtomFamily(loadableId))
    return state.hiddenTestcaseIds.size
})

/**
 * Number of locally-added (new) testcase IDs.
 *
 * Changes when the user adds or removes a new testcase row.
 * Used by the OSS URL layer to trigger snapshot re-encoding
 * so that new rows persist across page reloads.
 */
export const newTestcaseCountAtom = atom((get) => {
    const newIds = get(testcaseMolecule.atoms.newIds)
    return newIds.length
})

/**
 * Hash of locally-added (new) testcase data.
 *
 * Changes when the user edits the content of a new testcase row.
 * Used by the OSS URL layer to trigger snapshot re-encoding
 * so that edited new row data persists across page reloads.
 */
export const newTestcaseDataHashAtom = atom((get) => {
    const newIds = get(testcaseMolecule.atoms.newIds)
    if (newIds.length === 0) return ""
    const parts = newIds.map((id) => {
        const entity = get(testcaseMolecule.data(id))
        return entity?.data ? JSON.stringify(entity.data) : ""
    })
    return parts.join("|")
})

/**
 * Active sessions using derived loadableId
 *
 * Convenience selector that uses the derived loadableId from primary node.
 *
 * @example
 * const sessions = useAtomValue(activeSessionsWithContextAtom)
 */
export const activeSessionsWithContextAtom = atom((get) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return []
    return get(activeSessionsAtomFamily(loadableId))
})

/**
 * Is compare mode using derived loadableId
 *
 * @example
 * const isCompareMode = useAtomValue(isCompareModeWithContextAtom)
 */
export const isCompareModeWithContextAtom = atom((get) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return false
    return get(activeSessionsAtomFamily(loadableId)).length > 1
})

/**
 * Is any executing using derived loadableId
 *
 * @example
 * const isExecuting = useAtomValue(isAnyExecutingWithContextAtom)
 */
export const isAnyExecutingWithContextAtom = atom((get) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return false
    return get(isAnyExecutingAtomFamily(loadableId))
})

/**
 * Execution progress using derived loadableId
 *
 * @example
 * const progress = useAtomValue(executionProgressWithContextAtom)
 */
export const executionProgressWithContextAtom = atom((get) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) {
        return {
            total: 0,
            completed: 0,
            running: 0,
            percentage: 0,
            isComplete: false,
            isRunning: false,
        }
    }
    return get(executionProgressAtomFamily(loadableId))
})

/**
 * Context-aware row data selector.
 *
 * Reads the row from the loadable linked to the current primary node.
 */
export const rowDataWithContextAtomFamily = atomFamily((rowId: string) =>
    atom((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return null
        const rows = get(loadableController.selectors.rows(loadableId))
        const row = rows.find((item) => item.id === rowId)
        if (!row) return null
        return {
            id: row.id,
            data: row.data,
        }
    }),
)

/**
 * Context-aware variable value for a specific row + variable key.
 *
 * Avoids over-subscribing to the full row data object — only re-renders
 * when the specific variable value changes.
 */
export const rowVariableValueAtomFamily = atomFamily(
    ({rowId, variableId}: {rowId: string; variableId: string}) =>
        atom((get) => {
            if (!variableId) return ""
            const row = get(rowDataWithContextAtomFamily(rowId))
            const value = row?.data?.[variableId]
            return toDisplayString(value)
        }),
)

/**
 * Atom that reads downstream evaluator node queries to ensure they're mounted.
 *
 * `atomWithQuery` only starts fetching when it has a React subscriber (is mounted).
 * `evaluatorExpectedColumnsAtom` reads configuration which depends on the query,
 * but reading via `get()` in derived atoms doesn't mount the query observer.
 * This atom acts as a bridge — subscribe to it from a React component to
 * ensure downstream node queries are active.
 */
export const downstreamNodeQueriesAtom = atom((get) => {
    const nodes = get(playgroundNodesAtom)
    const downstreamNodes = nodes.filter((n) => n.depth > 0)
    for (const node of downstreamNodes) {
        get(workflowMolecule.selectors.query(node.entityId))
    }
    return downstreamNodes.length
})

/**
 * Columns expected by downstream evaluator nodes (e.g., correct_answer).
 * Scans evaluator configuration for `*_key` settings that map to testcase columns.
 * Mirrors the resolution logic in buildFromSchema (runnable/utils.ts).
 */
const evaluatorExpectedColumnsAtom = atom<string[]>((get) => {
    const nodes = get(playgroundNodesAtom)
    const downstreamNodes = nodes.filter((n) => n.depth > 0)

    const columns: string[] = []
    const seen = new Set<string>()

    for (const node of downstreamNodes) {
        const config = get(workflowMolecule.selectors.configuration(node.entityId))
        if (!config) continue

        // Scan top-level and nested objects (e.g., advanced_config)
        // for `*_key` settings that map to testcase columns.
        const entries: [string, unknown][] = Object.entries(config)
        for (const [key, value] of entries) {
            // Recurse one level into plain objects (advanced_config, etc.)
            if (value && typeof value === "object" && !Array.isArray(value)) {
                entries.push(...Object.entries(value as Record<string, unknown>))
            }
            if (!key.endsWith("_key") || typeof value !== "string" || !value) continue
            const columnName = value.startsWith("testcase.") ? value.split(".")[1] : value
            if (columnName && !seen.has(columnName)) {
                seen.add(columnName)
                columns.push(columnName)
            }
        }
    }
    return columns
})

/**
 * Context-aware variable keys for generation input rows.
 *
 * Keys are derived from the linked runnable columns, merged with
 * any additional columns expected by downstream evaluator nodes,
 * and finally merged with columns that exist in the actual testcase data.
 *
 * Uses atomFamily keyed on downstream entity IDs to ensure a fresh atom
 * is created when evaluators are connected/disconnected. This works around
 * a Jotai issue where module-level derived atoms don't re-evaluate when
 * playgroundNodesAtom changes in disconnect→reconnect flows.
 */
/**
 * Schema-referenced variable keys: prompt template input ports + downstream
 * evaluator expected columns. Excludes testcase-only extras.
 *
 * This is the "what does the prompt actually reference" view used by the
 * variable-visibility rule (`playgroundInputsAtomFamily` below): referenced
 * keys feed expanded variable cards; testcase columns NOT in this set get
 * collapsed under the "unused columns" footer.
 *
 * `rowVariableKeysAtomFamily` (below) wraps this with the testcase-extras
 * merge for callers that want the full "what's available" view.
 */
const referencedVariableKeysAtomFamily = atomFamily((downstreamKey: string) =>
    atom<string[]>((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return []
        // isChatModeAtom returns undefined while the entity query is in-flight.
        // Treating undefined as false (completion) would expose `messages` as a
        // variable card during the loading window. Return empty until the mode
        // is known so nothing flashes before data arrives.
        const isChatMode = get(isChatModeAtom)
        if (isChatMode === undefined) return []
        const isChat = isChatMode
        const columns = get(loadableController.selectors.columns(loadableId))
        const primaryKeys = columns.map((column) => column.key)
        const evaluatorKeys = get(evaluatorExpectedColumnsAtom)

        const keySet = new Set(primaryKeys)
        const merged = [...primaryKeys]
        for (const key of evaluatorKeys) {
            if (key && !keySet.has(key)) {
                keySet.add(key)
                merged.push(key)
            }
        }
        if (!isChat) return merged
        return merged.filter((key) => key !== "messages" && key !== "outputs")
    }),
)

const rowVariableKeysAtomFamily = atomFamily((downstreamKey: string) =>
    atom<string[]>((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return []
        const referencedKeys = get(referencedVariableKeysAtomFamily(downstreamKey))

        // In connected mode (testset loaded), include extra columns from
        // testcase entity data that aren't in the template (e.g. "expected_output").
        // In local mode, the template's input ports are the sole authority —
        // testcase data may contain stale keys from a previous template or app.
        const mode = get(loadableController.selectors.mode(loadableId))
        if (mode !== "connected") return referencedKeys

        const testcaseColumns = get(testcaseMolecule.atoms.columns) as {key: string}[]
        if (!testcaseColumns) return referencedKeys

        const keySet = new Set(referencedKeys)
        const merged = [...referencedKeys]
        for (const c of testcaseColumns) {
            if (!isSystemField(c.key) && !keySet.has(c.key)) {
                keySet.add(c.key)
                merged.push(c.key)
            }
        }
        return merged
    }),
)

/**
 * Backward-compatible selector that reads from the atomFamily with empty key.
 * For components that also subscribe to playgroundNodesAtom directly (e.g. SingleLayout),
 * use rowVariableKeysAtomFamily with a key derived from downstream node entity IDs
 * to avoid stale Jotai dependency tracking.
 */
export const rowVariableKeysWithContextAtom = rowVariableKeysAtomFamily("")

export {referencedVariableKeysAtomFamily, rowVariableKeysAtomFamily}

const draftReferencedVariableKeysAtom = atom<string[]>((get) => {
    const rootNodes = get(playgroundNodesAtom).filter((node) => node.depth === 0)

    const currentKeys = new Set<string>()
    const committedKeys = new Set<string>()
    let hasCommittedBaseline = false

    for (const node of rootNodes) {
        const serverData = get(workflowMolecule.selectors.serverData(node.entityId))
        const committed = collectCommittedInputKeys(serverData as WorkflowDataForInputKeys | null)
        if (!committed) continue

        hasCommittedBaseline = true
        for (const key of committed) committedKeys.add(key)

        const currentPorts = get(
            workflowMolecule.selectors.inputPorts(node.entityId),
        ) as RunnablePort[]
        for (const port of currentPorts) {
            if (port.key) currentKeys.add(port.key)
        }
    }

    if (!hasCommittedBaseline) return []
    return [...currentKeys].filter((key) => !committedKeys.has(key))
})

// ============================================================================
// PLAYGROUND INPUTS VISIBILITY (Step 4 of the playground mustache + input UX)
// ============================================================================

/**
 * The visibility view that `PlaygroundInputsBody` consumes: referenced
 * variables (with `undefined` values for those missing from the testcase) +
 * unreferenced testcase columns.
 *
 * Reactivity:
 *   referencedVariableKeysAtomFamily(downstreamKey)
 *        │
 *        ├──▶ playgroundInputsAtomFamily({testcaseId, downstreamKey})
 *        │       = atom((get) => splitInputsVisibility({
 *        │           referencedKeys: get(referenced...),
 *        │           testcaseData:   get(testcaseMolecule.atoms.data),
 *        │         }))
 *        │
 *   testcaseMolecule.atoms.data(testcaseId)
 *
 * System fields (`__id__`, etc.) are stripped from `testcaseData` before
 * the split so they don't bleed into the unused-columns footer.
 */
export interface PlaygroundInputsAtomKey {
    testcaseId: string
    /** Downstream-evaluator context key — pass the same string the caller
     *  uses with `rowVariableKeysAtomFamily` (`""` is the default). */
    downstreamKey?: string
}

export const playgroundInputsAtomFamily = atomFamily(
    ({testcaseId, downstreamKey = ""}: PlaygroundInputsAtomKey) =>
        atom((get) => {
            const referencedKeys = get(referencedVariableKeysAtomFamily(downstreamKey))
            const draftKeys = get(draftReferencedVariableKeysAtom)
            // isChatModeAtom is undefined while the entity query is in-flight.
            // Treating undefined as false would prevent `messages` / `outputs`
            // from being stripped from the testcase data during the loading
            // window, causing them to flash in the unreferenced-columns footer.
            // Return empty visibility until the mode is resolved.
            const isChatMode = get(isChatModeAtom)
            if (isChatMode === undefined) return {inputs: [], unreferencedColumns: []}
            const isChat = isChatMode
            // `testcaseMolecule.data(id)` returns the testcase ENTITY
            // (`{id, data, flags, tags, meta, ...}`), not the row data
            // dict. The actual column values live at `entity.data` — see
            // `testcaseDataAtomFamily` below for the canonical pattern.
            const entity = get(testcaseMolecule.data(testcaseId)) as {
                data?: Record<string, unknown>
            } | null
            const raw = entity?.data ?? {}

            // Strip system fields up-front so the unused-columns footer
            // doesn't expose `__id__` and friends to the user.
            //
            // In chat mode we ALSO strip `messages` and `outputs` — those are
            // implicitly used by the chat UI (`messageIdsAtomFamily`) and the
            // run-output panel respectively, not by a `{{messages}}` template
            // token. `referencedVariableKeysAtomFamily` filters them out of
            // `referencedKeys` (so they don't render as input cards), but the
            // testcase entity still carries them as data, so without this
            // strip they leak into the unreferenced-columns footer ("1 unused
            // testcase column hidden — `messages`"). Mahmoud reported this
            // confusion in QA on 2026-06-01.
            const testcaseData: Record<string, unknown> = {}
            for (const [key, value] of Object.entries(raw)) {
                if (isSystemField(key)) continue
                if (isChat && (key === "messages" || key === "outputs")) continue
                testcaseData[key] = value
            }

            // In connected mode, surface the test set's columns even when
            // this row doesn't carry them yet — a draft kept through "Keep
            // and load" or a row added while connected starts with only its
            // own keys. The drawer already offers these via the registry
            // merge in `rowVariableKeysAtomFamily`; without the same merge
            // here the grid hides exactly those fields. Same system/chat
            // strips as `testcaseData` above.
            let testsetColumnKeys: string[] | undefined
            const loadableId = get(derivedLoadableIdAtom)
            const mode = loadableId ? get(loadableController.selectors.mode(loadableId)) : null
            if (mode === "connected") {
                const registry = get(testcaseMolecule.atoms.columns) as {key: string}[] | null
                testsetColumnKeys = (registry ?? [])
                    .map((column) => column.key)
                    .filter(
                        (key) =>
                            !isSystemField(key) &&
                            !(isChat && (key === "messages" || key === "outputs")),
                    )
            }

            return splitInputsVisibility({
                referencedKeys,
                draftKeys,
                testcaseData,
                testsetColumnKeys,
            })
        }),
    (a, b) => a.testcaseId === b.testcaseId && (a.downstreamKey ?? "") === (b.downstreamKey ?? ""),
)

// ============================================================================
// DIRECT TESTCASE ENTITY SELECTORS
// ============================================================================

/**
 * Direct testcase cell value — reads a single field from the testcase entity.
 *
 * Uses testcaseMolecule.atoms.cell which is a selectAtom with equality check,
 * so it only re-renders when the specific cell value changes.
 *
 * This bypasses the loadable row indirection:
 *   OLD: rowVariableValueAtomFamily → rowDataWithContext → loadable.rows → .find() → toRow()
 *   NEW: testcaseMolecule.atoms.cell({id, column}) → selectAtom on entity
 *
 * Since rowId === testcaseId (loadableController always routes through testcaseMolecule),
 * the UI can address testcase entities directly.
 */
export const testcaseCellValueAtomFamily = atomFamily(
    ({testcaseId, column}: {testcaseId: string; column: string}) =>
        atom((get) => {
            if (!testcaseId || !column) return ""
            const value = get(testcaseMolecule.atoms.cell({id: testcaseId, column}))
            return toDisplayString(value)
        }),
    (a, b) => a.testcaseId === b.testcaseId && a.column === b.column,
)

/**
 * Direct testcase entity data — reads the full testcase entity.
 *
 * Returns the testcase's data record (the nested `data` property),
 * or null if the testcase doesn't exist.
 */
export const testcaseDataAtomFamily = atomFamily((testcaseId: string) =>
    atom((get) => {
        if (!testcaseId) return null
        const entity = get(testcaseMolecule.data(testcaseId))
        if (!entity) return null
        return {
            id: testcaseId,
            data: (entity as {data?: Record<string, unknown>}).data ?? {},
        }
    }),
)

// ============================================================================
// SESSION SELECTORS
// ============================================================================

/**
 * Check if in compare mode (multiple active sessions)
 *
 * @param loadableId - The loadable instance ID
 * @returns Atom that returns true if comparing multiple sessions
 */
export const isCompareModeAtomFamily = atomFamily((loadableId: string) =>
    atom((get) => get(activeSessionsAtomFamily(loadableId)).length > 1),
)

// ============================================================================
// RESULT SELECTORS
// ============================================================================

/**
 * Get execution progress for UI display
 *
 * @param loadableId - The loadable instance ID
 * @returns Atom for execution progress info
 */
export const executionProgressAtomFamily = atomFamily((loadableId: string) =>
    atom((get) => {
        const results = get(resultsByKeyAtomFamily(loadableId))
        const values = Object.values(results)

        const total = values.length
        const completed = values.filter(
            (r) => r.status === "success" || r.status === "error" || r.status === "cancelled",
        ).length
        const running = values.filter(
            (r) => r.status === "running" || r.status === "pending",
        ).length

        return {
            total,
            completed,
            running,
            percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
            isComplete: total > 0 && completed === total,
            isRunning: running > 0,
        }
    }),
)

// ============================================================================
// ROW-REVISION CONVENIENCE SELECTORS
// ============================================================================

/**
 * Key type for row-entity selectors.
 * Accepts either a string "rowId:entityId" or an object.
 */
type RowEntityKey = string | {rowId: string; entityId: string}

function parseRowEntityKey(param: RowEntityKey) {
    if (typeof param === "string") {
        const idx = param.indexOf(":")
        if (idx === -1) return {rowId: param, entityId: ""}
        return {rowId: param.slice(0, idx), entityId: param.slice(idx + 1)}
    }
    return param
}

function getExecutionItemLifecycleSnapshot(
    get: Getter,
    params: {rowId: string; entityId: string; loadableId?: string},
): ExecutionItemLifecycleSnapshot | null {
    const loadableId = params.loadableId || get(derivedLoadableIdAtom)
    if (!loadableId || !params.rowId || !params.entityId) return null

    const handle = createExecutionItemHandle({
        loadableId,
        rowId: params.rowId,
        entityId: params.entityId,
    })
    return handle.lifecycle.snapshot(get)
}

/**
 * Get the output/response for a row + entity.
 * Derives from execution state using loadableId from primary node.
 *
 * @param param - "rowId:entityId" string or {rowId, entityId} object
 * @returns Atom for the result output (or null)
 */
export const responseByRowEntityAtomFamily = ((param: RowEntityKey) => {
    const {rowId, entityId} = parseRowEntityKey(param)
    return atom((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return null
        const result = get(
            resultAtomFamily({loadableId, stepId: rowId, sessionId: `sess:${entityId}`}),
        )
        return result?.output ?? null
    })
}) as (param: RowEntityKey) => ReturnType<typeof atom<unknown>>

/**
 * Get the complete RunResult for a row + entity.
 * Exposes status, output, traceId, error, metrics, etc.
 *
 * @param param - "rowId:entityId" string or {rowId, entityId} object
 * @returns Atom for the full RunResult (or null)
 */
export const fullResultByRowEntityAtomFamily = ((param: RowEntityKey) => {
    const {rowId, entityId} = parseRowEntityKey(param)
    return atom((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return null
        return get(resultAtomFamily({loadableId, stepId: rowId, sessionId: `sess:${entityId}`}))
    })
}) as (param: RowEntityKey) => ReturnType<typeof atom<import("./types").RunResult | null>>

// ============================================================================
// RUN STATUS MAP
// ============================================================================

/**
 * Derived run status map keyed by `${rowId}:${entityId}`.
 *
 * Reads all results for the current loadable and reformats the package key
 * format (`stepId:sess:entityId`) into the consumer-friendly
 * `rowId:entityId` format.
 *
 * This is a read-only derivation — writes go through package atoms
 * (startRunAtom, completeRunAtom, etc.).
 */
export const runStatusByRowEntityAtom = selectAtom(
    atom((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return {}
        const all = get(resultsByKeyAtomFamily(loadableId))
        const mapped: Record<string, {isRunning?: string | false; resultHash?: string | null}> = {}
        for (const [key, result] of Object.entries(all)) {
            // Package key format: "stepId:sess:entityId"
            const sepIdx = key.indexOf(":sess:")
            if (sepIdx === -1) continue
            const stepId = key.slice(0, sepIdx)
            const entityId = key.slice(sepIdx + 5)
            const status = result?.status
            const isRunning = status === "running" || status === "pending"
            mapped[`${stepId}:${entityId}`] = {
                isRunning: isRunning ? result?.runId || "true" : false,
                resultHash: result?.resultHash ?? null,
            }
        }
        return mapped
    }),
    (v) => v,
    (a, b) => {
        const aKeys = Object.keys(a)
        const bKeys = Object.keys(b)
        if (aKeys.length !== bKeys.length) return false
        for (const k of aKeys) {
            const av = a[k]
            const bv = b[k]
            if (!bv || av?.isRunning !== bv?.isRunning || av?.resultHash !== bv?.resultHash)
                return false
        }
        return true
    },
)

// ============================================================================
// UNIFIED ROW ID SELECTORS
// ============================================================================

/**
 * Unified row IDs for generation rendering.
 *
 * - **completion mode**: returns loadable display row IDs (testcases)
 * - **chat mode**: returns shared (user) message IDs from flat model
 *
 * Replaces the OSS `generationLogicalTurnIdsAtom` / `generationRowIdsCompatAtom`.
 */
export const generationRowIdsAtom = atom<string[]>((get) => {
    const loadableId = get(derivedLoadableIdAtom)
    if (!loadableId) return []
    const isChat = get(isChatModeAtom)
    if (isChat) {
        const rowIds = get(sharedMessageIdsAtomFamily(loadableId))
        if (rowIds.length === 0) {
            // Bootstrap first blank user message for chat mode
            get(playgroundStoreAtom).set(addUserMessageAtom, {loadableId, userMessage: null})
            return get(sharedMessageIdsAtomFamily(loadableId))
        }
        return rowIds
    }
    if (isChat === undefined) return []

    // Read directly from the molecule's displayRowIds (global atom) and apply
    // the loadable's hidden-ID filter inline. This avoids going through
    // displayRowIdsAtomFamily (atomFamily) which can miss Jotai subscription
    // notifications when testcase IDs are replaced within a write transaction.
    const allDisplayRowIds = get(testcaseMolecule.atoms.displayRowIds)
    const state = get(loadableStateAtomFamily(loadableId))
    const hiddenIds = state.hiddenTestcaseIds
    if (hiddenIds.size === 0) return allDisplayRowIds
    return allDisplayRowIds.filter((id) => !hiddenIds.has(id))
})

/**
 * Unified execution row IDs.
 *
 * Alias of `generationRowIdsAtom` for clearer ownership semantics:
 * these IDs are the rows/turns that executions run against.
 */
export const executionRowIdsAtom = generationRowIdsAtom

// ============================================================================
// RENDERABLE EXECUTION ITEMS (UI SINGLE SOURCE OF TRUTH)
// ============================================================================

export interface RenderableExecutionItem {
    key: string
    rowId: string
    executionId: string
    rowIndex: number
    executionIndex: number
    isFirstRow: boolean
    isLastRow: boolean
    isFirstExecution: boolean
    isLastExecution: boolean
}

export interface RenderableExecutionRow {
    rowId: string
    rowIndex: number
    isFirstRow: boolean
    isLastRow: boolean
    items: RenderableExecutionItem[]
}

/**
 * Flattened renderable execution items for the current playground context.
 * Each item represents one row + executionId pair.
 */
export const renderableExecutionItemsAtom = atom<RenderableExecutionItem[]>((get) => {
    const rowIds = get(executionRowIdsAtom)
    const executionIds = get(displayedEntityIdsAtom)

    if (!Array.isArray(rowIds) || rowIds.length === 0) return []
    if (!Array.isArray(executionIds) || executionIds.length === 0) return []

    const items: RenderableExecutionItem[] = []
    for (let rowIndex = 0; rowIndex < rowIds.length; rowIndex++) {
        const rowId = rowIds[rowIndex]
        for (let executionIndex = 0; executionIndex < executionIds.length; executionIndex++) {
            const executionId = executionIds[executionIndex]
            items.push({
                key: `${rowId}:${executionId}`,
                rowId,
                executionId,
                rowIndex,
                executionIndex,
                isFirstRow: rowIndex === 0,
                isLastRow: rowIndex === rowIds.length - 1,
                isFirstExecution: executionIndex === 0,
                isLastExecution: executionIndex === executionIds.length - 1,
            })
        }
    }
    return items
})

/**
 * Renderable execution items grouped by row.
 */
export const renderableExecutionRowsAtom = atom<RenderableExecutionRow[]>((get) => {
    const rowIds = get(executionRowIdsAtom)
    const items = get(renderableExecutionItemsAtom)

    // Group items by rowId in a single pass
    const itemsByRow = new Map<string, RenderableExecutionItem[]>()
    for (const item of items) {
        const list = itemsByRow.get(item.rowId)
        if (list) {
            list.push(item)
        } else {
            itemsByRow.set(item.rowId, [item])
        }
    }

    return rowIds.map((rowId, rowIndex) => ({
        rowId,
        rowIndex,
        isFirstRow: rowIndex === 0,
        isLastRow: rowIndex === rowIds.length - 1,
        items: itemsByRow.get(rowId) ?? [],
    }))
})

/**
 * Renderable execution items scoped to a single row.
 */
export const renderableExecutionItemsByRowAtomFamily = atomFamily((rowId: string) =>
    atom((get) => get(renderableExecutionItemsAtom).filter((item) => item.rowId === rowId)),
)

/**
 * Renderable execution items scoped to a single execution ID (entity).
 */
export const renderableExecutionItemsByExecutionIdAtomFamily = atomFamily((executionId: string) =>
    atom((get) =>
        get(renderableExecutionItemsAtom).filter((item) => item.executionId === executionId),
    ),
)

/**
 * Deduplicated row IDs for a specific entity (execution) ID.
 *
 * When entityId is provided, filters renderable items to that entity and
 * extracts unique row IDs. When empty, falls back to the global executionRowIds.
 */
export const executionRowIdsForEntityAtomFamily = atomFamily((entityId: string) =>
    atom((get) => {
        if (!entityId) return get(executionRowIdsAtom) as string[]
        const items = get(renderableExecutionItemsByExecutionIdAtomFamily(entityId))
        const seen = new Set<string>()
        const ids: string[] = []
        for (const item of items) {
            if (!seen.has(item.rowId)) {
                seen.add(item.rowId)
                ids.push(item.rowId)
            }
        }
        return ids
    }),
)

// ============================================================================
// VARIABLE NAMES (derived from entity input ports)
// ============================================================================

/**
 * Unified variable names across all displayed entities.
 *
 * Reads input ports from each entity (revision) in the playground and
 * deduplicates by key. This is the single source of truth for which
 * variables exist — consumers should never store variable names separately.
 *
 * Variable names come from the runnable's input ports, which are derived
 * from the prompt template, tools, response_format, and schema.
 */
export const inputVariableNamesAtom = atom<string[]>((get) => {
    const nodes = get(playgroundNodesAtom).filter((n) => n.depth === 0)
    const isChat = get(isChatModeAtom) === true
    const seen = new Set<string>()
    const names: string[] = []
    for (const node of nodes) {
        const ports = get(workflowMolecule.selectors.inputPorts(node.entityId)) as RunnablePort[]
        for (const port of ports || []) {
            if (isChat && port.key === "messages") {
                continue
            }
            if (port.key && !seen.has(port.key)) {
                seen.add(port.key)
                names.push(port.key)
            }
        }
    }
    return names
})

/**
 * Schema map for input variables.
 *
 * Maps each variable key to its RunnablePort type and full schema.
 * Used by VariableControlAdapter to render schema-aware controls
 * (e.g. JSON editor for object types, number input for numbers).
 */
export const inputPortSchemaMapAtom = atom<
    Record<string, {type: string; name?: string; schema?: unknown; helpText?: string}>
>((get) => {
    const nodes = get(playgroundNodesAtom).filter((n) => n.depth === 0)
    const map: Record<string, {type: string; name?: string; schema?: unknown; helpText?: string}> =
        {}
    for (const node of nodes) {
        const ports = get(workflowMolecule.selectors.inputPorts(node.entityId)) as RunnablePort[]
        for (const port of ports || []) {
            if (port.key && !(port.key in map)) {
                map[port.key] = {
                    type: port.type,
                    name: port.name,
                    schema: port.schema,
                    helpText: port.helpText,
                }
            }
        }
    }
    return map
})

/**
 * Schema map for output variables (mirrors `inputPortSchemaMapAtom`).
 *
 * Sources:
 *   - Declared schema at `entity.data.schemas.outputs` (structured apps).
 *   - Inferred from trace metadata for ephemeral / custom workflows —
 *     see `outputPortsAtomFamily` in the workflow molecule for the
 *     derivation. Either way we surface ports here so the editor's
 *     JSONPath typeahead can suggest `$.outputs.<field>` paths.
 *
 * Aggregates across ALL nodes in the current playground (apps +
 * evaluators at depth > 0), because an evaluator prompt commonly
 * references `$.outputs.*` pointing at the subject app's outputs.
 */
export const outputPortSchemaMapAtom = atom<
    Record<string, {type: string; name?: string; schema?: unknown}>
>((get) => {
    const nodes = get(playgroundNodesAtom)
    const map: Record<string, {type: string; name?: string; schema?: unknown}> = {}
    for (const node of nodes) {
        const ports = get(workflowMolecule.selectors.outputPorts(node.entityId)) as RunnablePort[]
        for (const port of ports || []) {
            if (port.key && !(port.key in map)) {
                map[port.key] = {type: port.type, name: port.name, schema: port.schema}
            }
        }
    }
    return map
})

// ============================================================================
// COMPARISON STATE (derived from playground nodes)
// ============================================================================

// ============================================================================
// VARIABLE MERGE (PropertyNode-aware)
// ============================================================================

// ============================================================================
// APP-LEVEL MODE SELECTORS
// ============================================================================

/**
 * Playground-level chat behavior.
 *
 * Combines the app's capability (the primary node's
 * `workflowMolecule.selectors.executionMode`) with the user's playground
 * mode override (`playgroundModeOverrideAtom`). Completion apps cannot run
 * conversations, so the override only applies to chat-capable apps.
 * Returns `true` for chat behavior, `false` for completion behavior,
 * `undefined` while the playground has no root node.
 *
 * Request shapes follow the capability, not this atom (see
 * docs/design/playground-mode-switch/).
 */
export const isChatModeAtom = atom<boolean | undefined>((get) => get(playgroundIsChatBehaviorAtom))

/**
 * Per-entity agent-mode flag — true when THIS entity is an agent workflow.
 *
 * Deliberately **per-entity**, not app-scoped like `isChatModeAtom`
 * (`playgroundIsChatBehaviorAtom` reads the depth-0 node + a per-app override,
 * one value per playground). `ExecutionItems` renders one panel per `entityId`,
 * so the agent branch must answer "is THIS entity an agent", not "is the
 * playground in agent mode" — an app-scoped flag would misroute any mixed-type
 * comparison grid.
 *
 * Agent and chat are parallel, disjoint workflow types (agent-type →
 * AgentChatPanel; chat-type → ChatMode); agent mode is NOT a replacement for
 * classic chat mode.
 *
 * Signal: `workflowType === "agent"`, which derives from the backend `is_agent` flag.
 */
export const isAgentModeAtomFamily = atomFamily((entityId: string) =>
    atom<boolean>((get) => {
        // The backend marks the workflow as an agent (is_agent, surfaced via workflowType).
        return get(workflowMolecule.selectors.workflowType(entityId)) === "agent"
    }),
)

/**
 * App-level type derived from chat mode.
 *
 * Returns `"chat"` or `"completion"` based on `isChatModeAtom`.
 * Returns `undefined` while entity data is loading.
 */
export type AppType = "chat" | "completion"

export const appTypeAtom = atom<AppType | undefined>((get) => {
    const isChat = get(isChatModeAtom)
    if (isChat === undefined) return undefined
    return isChat ? "chat" : "completion"
})

// ============================================================================
// ROW RUN STATUS
// ============================================================================

/**
 * Check if any entity is currently running for a given row ID.
 *
 * Checks execution lifecycle snapshots across all displayed entity IDs.
 * Used by chat and completion UI to disable/enable run buttons during execution.
 */
export const isAnyRunningForRowAtomFamily = atomFamily((rowId: string) =>
    atom((get) => {
        const loadableId = get(derivedLoadableIdAtom)
        if (!loadableId) return false
        const ids = get(entityIdsAtom)
        return ids.some((eid: string) => {
            const lifecycle = getExecutionItemLifecycleSnapshot(get, {
                loadableId,
                rowId,
                entityId: eid,
            })
            return Boolean(lifecycle?.isRunning)
        })
    }),
)
